import React, { useState, useEffect } from 'react';
import { Settings, GripVertical, Eye, EyeOff, ChevronUp, ChevronDown, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_WIDGETS = [
  { id: 'progress', label: 'Fremdrift', enabled: true },
  { id: 'quick_actions', label: 'Hurtighandlinger', enabled: true },
  { id: 'summary', label: 'Nøkkeltall', enabled: true },
  { id: 'open_cases', label: 'Åpne saker', enabled: true },
  { id: 'upcoming_tasks', label: 'Kommende oppgaver', enabled: true },
  { id: 'gap_analysis', label: 'Aktive GAP-analyser', enabled: true },
  { id: 'iso_activation', label: 'ISO-aktivering', enabled: true },
  { id: 'mf_activation', label: 'Miljøfyrtårn-aktivering', enabled: true },
];

export function useDashboardWidgets() {
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadWidgets = async () => {
      try {
        const token = localStorage.getItem('systemklart_token');
        const res = await axios.get(`${API}/api/v2/auth/users/me/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.dashboard_widgets) {
          // Merge saved config with defaults (in case new widgets were added)
          const saved = res.data.dashboard_widgets;
          const merged = DEFAULT_WIDGETS.map(dw => {
            const s = saved.find(sw => sw.id === dw.id);
            return s ? { ...dw, enabled: s.enabled, order: s.order } : dw;
          });
          // Sort by order if available
          merged.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
          setWidgets(merged);
        }
      } catch (e) {
        // Use defaults
      } finally {
        setLoaded(true);
      }
    };
    loadWidgets();
  }, []);

  const saveWidgets = async (newWidgets) => {
    setWidgets(newWidgets);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.patch(`${API}/api/v2/auth/users/me/preferences`, {
        dashboard_widgets: newWidgets.map((w, i) => ({ id: w.id, enabled: w.enabled, order: i }))
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      console.error('Failed to save widget preferences:', e);
    }
  };

  const isWidgetEnabled = (id) => {
    const w = widgets.find(w => w.id === id);
    return w ? w.enabled : true;
  };

  const getWidgetOrder = () => widgets.filter(w => w.enabled).map(w => w.id);

  return { widgets, saveWidgets, isWidgetEnabled, getWidgetOrder, loaded };
}

export function WidgetConfigurator({ widgets, onSave, onClose }) {
  const [localWidgets, setLocalWidgets] = useState([...widgets]);

  const toggleWidget = (id) => {
    setLocalWidgets(prev => prev.map(w => w.id === id ? { ...w, enabled: !w.enabled } : w));
  };

  const moveWidget = (index, direction) => {
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= localWidgets.length) return;
    const updated = [...localWidgets];
    [updated[index], updated[newIdx]] = [updated[newIdx], updated[index]];
    setLocalWidgets(updated);
  };

  const handleSave = () => {
    onSave(localWidgets);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="widget-configurator">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-cyan-400" />
            Tilpass dashboard
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-slate-400 mb-4">Velg hvilke widgets som vises, og endre rekkefølgen.</p>

        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {localWidgets.map((widget, i) => (
            <div
              key={widget.id}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                widget.enabled
                  ? 'bg-slate-800/60 border-slate-700/50'
                  : 'bg-slate-900/40 border-slate-800/30 opacity-50'
              }`}
              data-testid={`widget-config-${widget.id}`}
            >
              <GripVertical className="w-4 h-4 text-slate-600 flex-shrink-0" />
              
              <span className="flex-1 text-sm text-slate-200">{widget.label}</span>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => moveWidget(i, -1)}
                  disabled={i === 0}
                  className="p-1 text-slate-500 hover:text-white disabled:opacity-20"
                  data-testid={`widget-up-${widget.id}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveWidget(i, 1)}
                  disabled={i === localWidgets.length - 1}
                  className="p-1 text-slate-500 hover:text-white disabled:opacity-20"
                  data-testid={`widget-down-${widget.id}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  onClick={() => toggleWidget(widget.id)}
                  className={`p-1.5 rounded-lg transition-all ${
                    widget.enabled ? 'text-cyan-400 hover:bg-cyan-500/10' : 'text-slate-600 hover:text-slate-400'
                  }`}
                  data-testid={`widget-toggle-${widget.id}`}
                >
                  {widget.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-5 pt-4 border-t border-slate-800">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-700"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2.5 bg-cyan-600 text-white rounded-xl text-sm font-medium hover:bg-cyan-500"
            data-testid="widget-save-btn"
          >
            Lagre
          </button>
        </div>
      </div>
    </div>
  );
}
